import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Chip,
    Stack,
    alpha,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Button
} from '@mui/material';
import {
    Edit as EditIcon,
    Assessment as AssessmentIcon,
    Visibility as VisibilityIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import CommonTable from '../UI/Table/CommonTable';
import { getAllClients, getDropdownData, getAssignments, getUsers } from '../Queries';
import RatingMetricsDialog from './RatingMetricsDialog';
import RatingDialog from './RatingDialog';
import ResultsChartDialog from './ResultsChartDialog';
import PrintRatingReport from './PrintRatingReport';

const ProgramTable = () => {
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allPrograms, setAllPrograms] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [selectedProgramCode, setSelectedProgramCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [openRatingMetrics, setOpenRatingMetrics] = useState(false);
    const [openRatingDialog, setOpenRatingDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedSessionType, setSelectedSessionType] = useState('');
    const [openResultsChart, setOpenResultsChart] = useState(false);
    const [selectedUserForResults, setSelectedUserForResults] = useState(null);
    
    // Refs for print components
    const initialReportRef = useRef();
    const finalReportRef = useRef();

    let [userData] = useState(localStorage.getItem("userData"));
    userData = userData ? JSON.parse(userData) : userData;

    // Load initial data
    useEffect(() => {
        loadClientsAndPrograms();
    }, []);

    const loadClientsAndPrograms = async () => {
        setIsLoading(true);
        try {
            const [clientsData, dropdownData, assignmentsData, usersData] = await Promise.all([
                getAllClients(),
                getDropdownData(),
                getAssignments(),
                getUsers()
            ]);

            // Process clients
            const processedClients = clientsData || [];
            setClients(processedClients);

            // Count assessments per program code
            const assessmentCounts = {};
            assignmentsData.forEach(assignment => {
                if (assignment.program_code && assignment.assignment_topic === 'assessment') {
                    // Handle comma-separated program codes
                    const programCodes = assignment.program_code.split(',').map(code => code.trim());
                    programCodes.forEach(code => {
                        assessmentCounts[code] = (assessmentCounts[code] || 0) + 1;
                    });
                }
            });

            // Count users per program code
            const userCounts = {};
            usersData.forEach(user => {
                if (user.role === "User" && user.programCode) {
                    // Handle comma-separated or array program codes
                    const codes = Array.isArray(user.programCode)
                        ? user.programCode
                        : user.programCode.split(',').map(c => c.trim());

                    codes.forEach(code => {
                        if (code) {
                            userCounts[code] = (userCounts[code] || 0) + 1;
                        }
                    });
                }
            });

            // Process programs from dropdown data
            const programsFromDropdown = dropdownData
                .filter(item => item.type === 'program_code')
                .map(item => ({
                    id: item.row_id,
                    program_code: item.name,
                    program_name: item.name,
                    description: item.json_data?.description || '',
                    status: 'Active',
                    client_code: item.client_code || '',
                    created_date: item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : '',
                    assessments_count: assessmentCounts[item.name] || 0,
                    users_count: userCounts[item.name] || 0
                }));

            setAllPrograms(programsFromDropdown);
            setAllUsers(usersData);

            // Set default selections
            if (processedClients.length > 0) {
                setSelectedClient(processedClients[0].client_code);

                // Filter programs for first client
                const clientPrograms = programsFromDropdown.filter(
                    p => p.client_code === processedClients[0].client_code
                );

                if (clientPrograms.length > 0) {
                    const firstProgramCode = clientPrograms[0].program_code;
                    setSelectedProgramCode(firstProgramCode);

                    // Filter users for the first program
                    const programUsers = usersData.filter(user => {
                        if (user.role === "User" && user.programCode) {
                            const codes = Array.isArray(user.programCode)
                                ? user.programCode
                                : user.programCode.split(',').map(c => c.trim());
                            return codes.includes(firstProgramCode);
                        }
                        return false;
                    });
                    setUsers(programUsers);
                } else {
                    setUsers([]);
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle client selection change
    const handleClientChange = (clientCode) => {
        setSelectedClient(clientCode);

        // Filter programs for selected client
        const clientPrograms = allPrograms.filter(p => p.client_code === clientCode);

        // Set first program as selected if available
        if (clientPrograms.length > 0) {
            const firstProgramCode = clientPrograms[0].program_code;
            setSelectedProgramCode(firstProgramCode);

            // Filter users for the first program
            const programUsers = allUsers.filter(user => {
                if (user.role === "User" && user.programCode) {
                    const codes = Array.isArray(user.programCode)
                        ? user.programCode
                        : user.programCode.split(',').map(c => c.trim());
                    return codes.includes(firstProgramCode);
                }
                return false;
            });
            setUsers(programUsers);
        } else {
            setSelectedProgramCode('');
            setUsers([]);
        }
    };

    // Handle program selection change
    const handleProgramChange = (programCode) => {
        setSelectedProgramCode(programCode);

        // Filter users for selected program
        const programUsers = allUsers.filter(user => {
            if (user.role === "User" && user.programCode) {
                const codes = Array.isArray(user.programCode)
                    ? user.programCode
                    : user.programCode.split(',').map(c => c.trim());
                return codes.includes(programCode);
            }
            return false;
        });
        setUsers(programUsers);
    };

    // Get programs for selected client (remove duplicates)
    const availableProgramsForClient = useMemo(() => {
        const filtered = allPrograms.filter(p => p.client_code === selectedClient);
        // Remove duplicates based on program_code
        const uniquePrograms = filtered.reduce((acc, current) => {
            const exists = acc.find(item => item.program_code === current.program_code);
            if (!exists) {
                acc.push(current);
            }
            return acc;
        }, []);
        return uniquePrograms;
    }, [allPrograms, selectedClient]);

    // Define columns
    const columns = useMemo(() => {
        return [
            { key: 'serialNo', label: 'S.No', minWidth: 50, align: 'center' },
            { key: 'name', label: 'Name', minWidth: 140 },
            { key: 'email', label: 'Email', minWidth: 180 },
            { key: 'pre_program_review', label: 'Initial', minWidth: 120, align: 'center' },
            { key: 'post_program_review', label: 'Final', minWidth: 120, align: 'center' },
            { key: 'results', label: 'Results', minWidth: 80, align: 'center' }
        ];
    }, []);

    // Handle edit action (opens rating dialog)
    const handleEdit = (row, sessionType) => {
        setSelectedUser(row);
        setSelectedSessionType(sessionType);
        setOpenRatingDialog(true);
    };

    // Handle view results action
    const handleViewResults = (row) => {
        setSelectedUserForResults(row);
        setOpenResultsChart(true);
    };

    // Render action buttons for session columns
    const renderActionButtons = (row, sessionType) => {
        return (
            <Stack direction="row" spacing={0.5} justifyContent="center">
                <Tooltip title="Edit" arrow placement="top">
                    <IconButton
                        onClick={() => handleEdit(row, sessionType)}
                        size="small"
                        sx={{
                            padding: '5px',
                            backgroundColor: alpha('#cc4049', 0.1),
                            color: '#cc4049',
                            border: `1px solid ${alpha('#cc4049', 0.3)}`,
                            borderRadius: '6px',
                            '&:hover': {
                                backgroundColor: alpha('#cc4049', 0.2),
                                transform: 'scale(1.1)',
                                boxShadow: '0 2px 8px rgba(204, 64, 73, 0.3)',
                            },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
        );
    };

    // Cell rendering function
    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: '0.85rem'
                    }}>
                        {index + 1}
                    </Box>
                );

            case 'name':
                return (
                    <Box>
                        <Box sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: '#2c3e50'
                        }}>
                            {row.name || '-'}
                        </Box>
                    </Box>
                );

            case 'email':
                return (
                    <Box sx={{
                        fontSize: '0.8rem',
                        color: 'text.secondary'
                    }}>
                        {row.email || '-'}
                    </Box>
                );

            case 'pre_program_review':
                return renderActionButtons(row, 'pre_program_review');

            case 'post_program_review':
                return renderActionButtons(row, 'post_program_review');

            case 'results':
                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Results" arrow placement="top">
                            <IconButton
                                onClick={() => handleViewResults(row)}
                                size="small"
                                sx={{
                                    padding: '5px',
                                    backgroundColor: alpha('#17a2b8', 0.1),
                                    color: '#17a2b8',
                                    border: `1px solid ${alpha('#17a2b8', 0.3)}`,
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: alpha('#17a2b8', 0.2),
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <VisibilityIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );

            default:
                return value;
        }
    };

    // Print handler for Initial Report
    const handleInitialReportPrint = useReactToPrint({
        content: () => initialReportRef.current,
        documentTitle: `Initial_Rating_Report_${selectedProgramCode}_${new Date().toLocaleDateString().replace(/\//g, '-')}`,
        pageStyle: `
            @page {
                size: A4;
                margin: 15mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                .print-wrapper {
                    font-family: Arial, sans-serif;
                }
                .page-break {
                    page-break-after: always;
                }
                .no-break {
                    page-break-inside: avoid;
                }
                .print-header {
                    margin-bottom: 20px;
                }
                .print-footer {
                    position: fixed;
                    bottom: 0;
                    width: 100%;
                    text-align: center;
                    font-size: 10px;
                    color: #666;
                    padding: 10px 0;
                    border-top: 1px solid #e0e0e0;
                }
            }
        `
    });

    // Print handler for Final Report
    const handleFinalReportPrint = useReactToPrint({
        content: () => finalReportRef.current,
        documentTitle: `Final_Rating_Report_${selectedProgramCode}_${new Date().toLocaleDateString().replace(/\//g, '-')}`,
        pageStyle: `
            @page {
                size: A4;
                margin: 15mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                .print-wrapper {
                    font-family: Arial, sans-serif;
                }
                .page-break {
                    page-break-after: always;
                }
                .no-break {
                    page-break-inside: avoid;
                }
                .print-header {
                    margin-bottom: 20px;
                }
                .print-footer {
                    position: fixed;
                    bottom: 0;
                    width: 100%;
                    text-align: center;
                    font-size: 10px;
                    color: #666;
                    padding: 10px 0;
                    border-top: 1px solid #e0e0e0;
                }
            }
        `
    });

    return (
        <>
            <CommonTable
                columns={columns}
                data={users}
                renderCustomCell={renderCell}
                isLoading={isLoading}
                emptyMessage="No users assigned to this program"
                actionButtons={
                    <Stack direction="row" spacing={2} alignItems="center">
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel sx={{ fontFamily: 'Poppins' }}>Select Client</InputLabel>
                            <Select
                                value={selectedClient}
                                label="Select Client"
                                onChange={(e) => handleClientChange(e.target.value)}
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: '13px'
                                }}
                            >
                                {clients.map((client) => (
                                    <MenuItem
                                        key={client.client_code}
                                        value={client.client_code}
                                        sx={{ fontFamily: 'Poppins' }}
                                    >
                                        {client.client_code}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel sx={{ fontFamily: 'Poppins' }}>Select Program</InputLabel>
                            <Select
                                value={selectedProgramCode}
                                label="Select Program"
                                onChange={(e) => handleProgramChange(e.target.value)}
                                disabled={availableProgramsForClient.length === 0}
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: '13px'
                                }}
                            >
                                {availableProgramsForClient.map((program) => (
                                    <MenuItem
                                        key={program.program_code}
                                        value={program.program_code}
                                        sx={{ fontFamily: 'Poppins' }}
                                    >
                                        {program.program_code}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                            onClick={handleInitialReportPrint}
                            disabled={!selectedProgramCode || users.length === 0}
                            size="small"
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderRadius: 1,
                                px: 2,
                                fontSize: '13px',
                                py: 0.75,
                                background: "linear-gradient(45deg, #28a745 30%, #20c997 90%)",
                                "&:hover": {
                                    background: "linear-gradient(45deg, #218838 30%, #1aa179 90%)",
                                },
                                "&:disabled": {
                                    opacity: 0.5
                                }
                            }}
                        >
                            Initial Report
                        </Button>

                        {/* <Button
                            variant="contained"
                            startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                            onClick={handleFinalReportPrint}
                            disabled={!selectedProgramCode || users.length === 0}
                            size="small"
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderRadius: 1,
                                px: 2,
                                fontSize: '13px',
                                py: 0.75,
                                background: "linear-gradient(45deg, #007bff 30%, #0056b3 90%)",
                                "&:hover": {
                                    background: "linear-gradient(45deg, #0056b3 30%, #003d82 90%)",
                                },
                                "&:disabled": {
                                    opacity: 0.5
                                }
                            }}
                        >
                            Final Report
                        </Button> */}

                        <Button
                            variant="contained"
                            startIcon={<AssessmentIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setOpenRatingMetrics(true)}
                            disabled={!selectedProgramCode}
                            size="small"
                            sx={{
                                textTransform: "none",
                                fontWeight: 500,
                                borderRadius: 1,
                                px: 2,
                                fontSize: '13px',
                                py: 0.75,
                                background: "linear-gradient(45deg, #0a0068 30%, #cc4049 90%)",
                                "&:hover": {
                                    background: "linear-gradient(45deg, #1565c0 30%, #0a0068 90%)",
                                },
                                "&:disabled": {
                                    opacity: 0.5
                                }
                            }}
                        >
                            Rating Metrics
                        </Button>
                    </Stack>
                }
            />

            <RatingMetricsDialog
                open={openRatingMetrics}
                onClose={() => setOpenRatingMetrics(false)}
                programCode={selectedProgramCode}
                clientCode={selectedClient}
            />

            <RatingDialog
                open={openRatingDialog}
                onClose={() => {
                    setOpenRatingDialog(false);
                    setSelectedUser(null);
                    setSelectedSessionType('');
                }}
                user={selectedUser}
                sessionType={selectedSessionType}
                programCode={selectedProgramCode}
                clientCode={selectedClient}
            />

            <ResultsChartDialog
                open={openResultsChart}
                onClose={() => {
                    setOpenResultsChart(false);
                    setSelectedUserForResults(null);
                }}
                user={selectedUserForResults}
                programCode={selectedProgramCode}
                clientCode={selectedClient}
            />

            {/* Hidden print components */}
            <div style={{ display: 'none' }}>
                <PrintRatingReport
                    ref={initialReportRef}
                    users={users}
                    programCode={selectedProgramCode}
                    clientCode={selectedClient}
                    reportType="initial"
                />
            </div>
            
            <div style={{ display: 'none' }}>
                <PrintRatingReport
                    ref={finalReportRef}
                    users={users}
                    programCode={selectedProgramCode}
                    clientCode={selectedClient}
                    reportType="final"
                />
            </div>
        </>
    );
};

export default ProgramTable;
